/**
 * External Imports
 */
import {
  FC,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  MouseEvent
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { NeuButton, NeuRadio } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import RoundingTypeDropdown from '../../customDropdowns/roundingTypeDropdown';
import { BlueChip } from '../../shared/chips/blueChip';
import DeleteBtn from '../../shared/buttons/DeleteBtn';
import { Dropdown } from '../../shared/dropdowns';
import EmployeeSearch from '../reusableModalPieces/employeeSearch';
import FeedbackModalView from './FeedbackModalView';
import PatientSearch from '../reusableModalPieces/patientSearch';
import { RootState } from '../../../redux/store';
import {
  clearPatientSearch,
  getRound,
  setPendingFeedback,
  clearRoundState,
  addTaskToSharingQ,
  completeTask,
  getCategoriesByType,
  postNewTask,
  resetSelectedDataForEdit
} from '../../../redux/actions';
import {
  useLazyGetEmployeesByIdsQuery,
  useLazyGetEmployeesBySearchQuery
} from '../../../redux/apis/Employees.api';

import { handleModal } from '../../../services/ModalPortal/utils';
import { useUserRolesRef } from '../../../services/UserRoles';

import { logAdHocClick, logTaskClick } from '../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../utils/debouncers';
import {
  createAnyId,
  date,
  formatRoundingType,
  reverseName,
  toTitleCase
} from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Category,
  Employee,
  Feedback,
  FeedbackPost,
  Patient,
  Unit
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CardHeader,
  ChipWrapper,
  FeedbackCard,
  FeedbackViewDiv,
  FeedbackViewDiv2Column,
  FeedbackViewSubDiv,
  InputHeading,
  LabelContentBold,
  LabelContentPatient,
  MarkCompleteButton,
  ModalCardContent,
  ModalFooter,
  ModalTextArea,
  SearchResultsContainer,
  StyledDivider,
  TitleSpan,
  TitleIcon
} from './FeedbackModal.styles';

/**
 * Constant Imports
 */
import feedbackTypes from '../../../constants/validRoundingTypesWithTasks.json';

interface IAddFeedbackModalProps {
  authorizedUser: AuthUserProps;
  categories: Category[];
  globalUserSection: string;
  isRoundStarted: boolean;
  modalAction: string;
  modalData: Feedback;
  modalType: string;
  selectedFeedback: Feedback | null;
  units: Unit[];
}
export interface EmployeeSharedWithProps {
  firstName: string;
  lastName: string;
  employeeId: string;
  fullName: string;
}

type RequiredItemTypes = {
  [key: string]: string | string[] | undefined;
};

const requiredItems: RequiredItemTypes = {
  taskSubtype: '',
  category: '',
  employeeFirstName: '',
  description: ''
};

const AddFeedbackModal: FC<IAddFeedbackModalProps> = ({
  authorizedUser,
  categories,
  globalUserSection,
  isRoundStarted,
  modalAction,
  modalData,
  modalType,
  selectedFeedback,
  units
}) => {
  const dispatch = useDispatch();

  const [shareObject, setShareObject] = useState<{
    taskId: string;
    userName: string;
    email: string[];
  }>({ taskId: '', userName: '', email: [] });

  const [selectedTaskSubtype, setSelectedTaskSubtype] = useState<string>('');
  const [selectedSubCategoryItem, setSelectedSubCategoryItem] = useState('');
  const [subCategories, setSubCategories] = useState<string[]>([]);

  const [requiredFields, setRequiredFields] = useState(requiredItems);

  const [showButton, setShowButton] = useState<boolean>(false);

  const [isRoundingTypeOpen, setIsRoundingTypeOpen] = useState(false);
  const [isUnitOpen, setIsUnitOpen] = useState(false);
  const [isCatOpen, setIsCatOpen] = useState(false);
  const [isSubcatOpen, setIsSubcatOpen] = useState(false);
  const [isSharedWithOpen, setIsSharedWithOpen] = useState<boolean>(true);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [sharedWithSearchTerm, setSharedWithSearchTerm] = useState('');

  const [taskObject, setTaskObject] = useState<FeedbackPost>({
    taskId: createAnyId('task', authorizedUser.facilityId),
    taskType: 'feedback',
    taskSubtype: '',
    completed: false,
    taskDate: date.now(),
    description: '',
    facilityId: authorizedUser.facilityId,
    roundingType: feedbackTypes.includes(globalUserSection)
      ? globalUserSection
      : '',
    sharedWith: [],
    userId: authorizedUser.hcaid,
    userFullName:
      authorizedUser.firstName && authorizedUser.lastName
        ? `${authorizedUser.firstName} ${authorizedUser.lastName}`
        : '',
    taskCategory: [],
    employeeId: '',
    category: '',
    subCategory: '',
    email: '',
    _source: 'web'
  });

  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  /** Employee search */
  const userAddedSharedWithEmpId = useRef<string[]>([]);

  // debouncing search term
  const dbSharedSearchTerm = useDebounceValue(sharedWithSearchTerm, 250);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const {
    isCSCRounder,
    isCNEdRounder,
    isEnterpriseAdmin,
    isNonSupportRounder
  } = useUserRolesRef();

  const { logTrackingEvent } = useAnalyticsApi();

  const [
    getEmployeesByIds,
    {
      data: sharedEmployeesData
      // isError: isGetEmployeesByIdsError,
      // isFetching: isGetEmployeesByIdsFetching,
      // isLoading: isGetEmployeesByIdsLoading
    }
  ] = useLazyGetEmployeesByIdsQuery();
  const [
    getEmployeesBySearch,
    {
      data: employeesBySearchData
      // isError: isGetEmployeesBySearchError,
      // isFetching: isGetEmployeesBySearchFetching,
      // isLoading: isGetEmployeesBySearchLoading
    }
  ] = useLazyGetEmployeesBySearchQuery();

  const setRequiredFieldValues = (taskObj: FeedbackPost) => {
    const {
      unitId,
      taskSubtype,
      category,
      subCategory,
      employeeFirstName,
      description
    } = taskObj;
    if (taskObject.roundingType === 'patient') {
      if (subCategories.length > 0) {
        setRequiredFields({
          unitId,
          taskSubtype: taskSubtype || selectedTaskSubtype,
          category,
          subCategory: selectedSubCategoryItem || subCategory,
          description,
          employeeFirstName
        });
      } else {
        setRequiredFields({
          unitId,
          taskSubtype: taskSubtype || selectedTaskSubtype,
          category,
          employeeFirstName,
          description
        });
      }
    } else if (subCategories.length > 0) {
      setRequiredFields({
        taskSubtype: taskSubtype || selectedTaskSubtype,
        category,
        subCategory: selectedSubCategoryItem || subCategory,
        description,
        employeeFirstName
      });
    } else {
      setRequiredFields({
        taskSubtype: taskSubtype || selectedTaskSubtype,
        category,
        employeeFirstName,
        description
      });
    }
  };

  const clearSelectedUser = () => {
    if (
      (modalAction === 'ADD' ||
        modalAction === 'UNIVERSAL-ADD' ||
        modalType === 'Add Patient Feedback On Rounds' ||
        modalType === 'Add Employee Feedback On Rounds' ||
        modalType === 'Add Support Feedback On Rounds') &&
      requiredFields.employeeFirstName
    ) {
      if (taskObject.employeeId !== '') {
        const tempShare: string[] = taskObject.sharedWith;
        setTaskObject({
          ...taskObject,
          sharedWith: tempShare.filter(
            emp => emp !== taskObject.employeeId.toUpperCase()
          ),
          employeeFirstName: '',
          employeeLastName: '',
          employeeId: '',
          employeeFullName: '',
          employeePosition: ''
        });
        const tempShareEmail = shareObject.email;
        setShareObject({
          taskId: taskObject.taskId,
          userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`,
          email: tempShareEmail.filter(
            (email: string) => taskObject.email !== email
          )
        });
      } else {
        setTaskObject({
          ...taskObject,
          employeeFirstName: '',
          employeeLastName: '',
          employeeId: '',
          employeeFullName: '',
          employeePosition: ''
        });
      }
      setRequiredFields({
        ...requiredFields,
        employeeFirstName: ''
      });
    }
  };

  const changeFeedbackOrigin = (type: string) => {
    setTaskObject({
      ...taskObject,
      roundingType: type,
      category: ''
    });
  };

  const handleSearching = (e: any) => {
    const target = e.target as HTMLInputElement;
    setSharedWithSearchTerm(target.value);
  };

  const handleInputs = (
    event: MouseEvent<HTMLNeuRadioElement> | CustomEvent<any>
  ) => {
    const target = event.target as HTMLNeuInputElement;
    const { name = '', value = '' } = target;

    if (name === 'taskSubtype') {
      setSelectedTaskSubtype(value);
      if (value === 'recognition') {
        const duplicateFlag =
          taskObject.sharedWith.includes(taskObject.employeeId.toUpperCase()) ||
          taskObject.sharedWith.includes(taskObject.employeeId.toString());
        if (taskObject.employeeId !== '' && !duplicateFlag) {
          setTaskObject({
            ...taskObject,
            category: '',
            subCategory: '',
            [name]: value,
            sharedWith: taskObject.sharedWith
              ? [
                  ...taskObject.sharedWith,
                  `${taskObject.employeeId.toUpperCase()}`
                ]
              : [`${taskObject.employeeId.toUpperCase()}`]
          });
          setShareObject({
            email: taskObject.email
              ? [...shareObject.email, taskObject.email]
              : [...shareObject.email],
            taskId: taskObject.taskId,
            userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`
          });
        } else if (taskObject.category === '') {
          setTaskObject({ ...taskObject, [name]: value });
        } else {
          setTaskObject({
            ...taskObject,
            category: '',
            subCategory: '',
            [name]: value
          });
        }
      } else if (
        taskObject.employeeId !== '' &&
        taskObject.sharedWith.length > 0 &&
        !userAddedSharedWithEmpId.current.includes(taskObject.employeeId)
      ) {
        const tempShared = taskObject.sharedWith;
        setTaskObject({
          ...taskObject,
          category: '',
          subCategory: '',
          [name]: value,
          sharedWith: tempShared.filter(
            emp =>
              emp.toUpperCase() !==
              taskObject.employeeId.toString().toUpperCase()
          )
        });
        setShareObject({
          email: shareObject.email.filter(
            (email: string) => taskObject.email !== email
          ),
          taskId: taskObject.taskId,
          userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`
        });
      } else if (taskObject.category === '') {
        setTaskObject({ ...taskObject, [name]: value });
      } else {
        setTaskObject({
          ...taskObject,
          category: '',
          subCategory: '',
          [name]: value
        });
      }
      setRequiredFieldValues({
        ...taskObject,
        category: '',
        subCategory: ''
      });
    }

    if (name === 'roundingType') {
      setTaskObject({ ...taskObject, roundingType: value });
    }
    if (name === 'description') {
      setTaskObject({ ...taskObject, [name]: value });
    }
    // For validation
    if (Object.keys(requiredFields).includes(name)) {
      setRequiredFields({ ...requiredFields, [name]: value });
    }
  };

  const handleDropdownChange = useCallback(
    (value: string, name: string) => {
      if (name === 'category') {
        setTaskObject({ ...taskObject, [name]: value, subCategory: '' });
      } else {
        setTaskObject({ ...taskObject, [name]: value });
      }
      if (name === 'subCategory') {
        setSelectedSubCategoryItem(value);
      }

      // For validation
      if (Object.keys(requiredFields).includes(name)) {
        setRequiredFields({ ...requiredFields, [name]: value });
      }
    },
    [requiredFields, taskObject]
  );

  const handleClose = () => {
    dispatch(clearPatientSearch());
    dispatch(resetSelectedDataForEdit());
    handleModal(modalContentRef);
  };

  const handlePost = () => {
    if (showButton) {
      if (modalAction === 'UNIVERSAL-ADD') {
        logTrackingEvent(logAdHocClick(modalType, 'save'));
      } else {
        logTrackingEvent(logTaskClick('feedback', 'save'));
      }
      const payload = {
        ...taskObject,
        roundingType:
          taskObject.roundingType !== 'csc' ? taskObject.roundingType : 'csrn'
      };
      delete payload.email;
      if (isRoundStarted && modalData && modalAction !== 'EDIT') {
        dispatch(setPendingFeedback(payload));
      } else {
        dispatch(postNewTask(payload));
      }
      if (
        shareObject.taskId &&
        shareObject.userName &&
        shareObject.email.length > 0
      ) {
        dispatch(addTaskToSharingQ(shareObject));
      }
      setTimeout(() => {
        handleClose();
      }, 500);
    }
  };

  const handleMarkCompleted = (feedback: Feedback) => {
    const postData = {
      taskId: feedback.taskId,
      edit: true,
      taskType: 'feedback'
    };
    dispatch(completeTask(postData));
    handleClose();
    // appInsights.trackEvent({
    //   name: 'Feedback_Completed'
    // });
  };

  const handleRemoveSharedWith = (emp: string) => {
    let removedEmpName: string;
    const spaces = emp.split(' ').length - 1;
    if (spaces === 2) {
      removedEmpName = emp.split(' ').slice(0, 2).join('.');
    } else {
      const splittedArray = emp.split(' ');
      removedEmpName = `${splittedArray[0] + splittedArray[1]}.${
        splittedArray[2]
      }`;
    }
    const removedEmpId = emp.split(/[\s+]+/).pop() || '';
    userAddedSharedWithEmpId.current = userAddedSharedWithEmpId.current.filter(
      (empId: string) => empId !== removedEmpId
    );
    if (taskObject.taskSubtype === 'recognition') {
      if (removedEmpId?.trim() !== taskObject.employeeId.toUpperCase()) {
        // Update sharedWith in task object
        const newSharedWith = taskObject.sharedWith.filter((item: string) => {
          return item.includes(removedEmpId) === false;
        });
        setTaskObject({
          ...taskObject,
          sharedWith: newSharedWith
        });

        // update Emails for sharing
        const emailsAfterRemove = shareObject.email.filter((email: string) => {
          const existingString = email.split('@')[0];
          return existingString !== removedEmpName;
        });
        setShareObject({ ...shareObject, email: emailsAfterRemove });
      }
    } else {
      // Update sharedWith in task object
      const newSharedWith = taskObject.sharedWith.filter((item: string) => {
        return item.includes(removedEmpId) === false;
      });
      setTaskObject({
        ...taskObject,
        sharedWith: newSharedWith
      });

      // update Emails for sharing
      const emailsAfterRemove = shareObject.email.filter((email: string) => {
        const existingString = email.split('@')[0];
        return existingString !== removedEmpName;
      });
      setShareObject({ ...shareObject, email: emailsAfterRemove });
    }
  };

  // Code block for navigating to round from issue modal
  const handleRedirect = () => {
    if (taskObject?.roundId) {
      if (globalUserSection === 'patient') {
        handleModal(modalContentRef);
        const patientMeta = {
          facilityId: taskObject.facilityId,
          accountNum: taskObject.accountNum,
          unitId: taskObject.unitId
        };
        if (taskObject.accountNum?.substring(0, 2) === 'UL') {
          navigate('/patients/profile', {
            state: {
              patientMeta: {
                ...patientMeta,
                firstName: taskObject.patientFirstName,
                lastName: taskObject.patientLastName,
                mrn: taskObject.patientMRN,
                room: taskObject.room,
                bed: taskObject.bed,
                lastRoundId: taskObject.roundId
              },
              isUnlistedPatient: true
            }
          });
        } else {
          navigate('/patients/profile', {
            state: {
              patientMeta,
              isUnlistedPatient: false
            }
          });
        }
      }
      if (globalUserSection === 'employee') {
        handleModal(modalContentRef);
        navigate('/employees/profile', {
          state: {
            from: pathname,
            employeeId: taskObject?.employeeId,
            mode: undefined
          }
        });
      }
      if (globalUserSection === 'csc' || globalUserSection === 'cned') {
        handleModal(modalContentRef);
        navigate(`/support/profile/${globalUserSection}`, {
          state: {
            from: pathname,
            employeeId: taskObject?.employeeId,
            mode: undefined
          }
        });
      }
    }
  };

  const handleRemoveChip = (type: string) => {
    if (type === 'patient') {
      setTaskObject({
        ...taskObject,
        room: '',
        accountNum: '',
        patientFirstName: '',
        patientLastName: '',
        patientFullName: ''
      });
    } else {
      clearSelectedUser();
      setTaskObject({
        ...taskObject,
        employeeFirstName: '',
        employeeLastName: '',
        employeeId: '',
        employeeFullName: '',
        employeePosition: '',
        employee: '',
        department: '',
        departmentCode: '',
        departmentId: ''
      });
      setRequiredFields({
        ...requiredFields,
        employeeFirstName: ''
      });
    }
  };

  const setEmployeeForFeedback = (emp: Employee) => {
    const empId = emp.employeeId.toUpperCase();
    if (taskObject.taskSubtype === 'recognition') {
      const duplicateFlag = taskObject.sharedWith.includes(empId);
      if (!duplicateFlag) {
        setTaskObject({
          ...taskObject,
          employeeFirstName: emp.firstName,
          employeeLastName: emp.lastName,
          employeeId: empId,
          employeeFullName: `${emp.firstName} ${emp.lastName}`,
          employeePosition: emp.title,
          email: emp.email,
          sharedWith: taskObject.sharedWith
            ? [...taskObject.sharedWith, `${empId}`]
            : [`${empId}`],
          department: emp.dept,
          departmentCode: emp.departmentCode,
          departmentId: emp.departmentId
        });
        setShareObject({
          email: [...shareObject.email, emp.email],
          taskId: taskObject.taskId,
          userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`
        });
      } else {
        setTaskObject({
          ...taskObject,
          employeeFirstName: emp.firstName,
          employeeLastName: emp.lastName,
          employeeId: emp.employeeId,
          employeeFullName: `${emp.firstName} ${emp.lastName}`,
          employeePosition: emp.title,
          email: emp.email,
          department: emp.dept,
          departmentCode: emp.departmentCode,
          departmentId: emp.departmentId
        });
      }
    } else {
      setTaskObject({
        ...taskObject,
        employeeFirstName: emp.firstName,
        employeeLastName: emp.lastName,
        employeeId: empId,
        employeeFullName: `${emp.firstName} ${emp.lastName}`,
        employeePosition: emp.title,
        email: emp.email,
        department: emp.dept,
        departmentCode: emp.departmentCode,
        departmentId: emp.departmentId
      });
    }
    // For validation
    setRequiredFields({
      ...requiredFields,
      employeeFirstName: emp.firstName
    });
  };

  const setPatientForFeedback = (patient: Patient) => {
    setTaskObject({
      ...taskObject,
      room: patient.room,
      bed: patient.bed,
      accountNum: patient.accountNumber as string,
      patientFirstName: patient.firstname as string,
      patientLastName: patient.lastname as string,
      patientFullName: `${patient.lastname}, ${patient.firstname}`
    });
  };

  const setEmployeeToShare = (emp: Employee) => {
    const empId = emp.employeeId.toUpperCase();
    if (!userAddedSharedWithEmpId.current.includes(empId)) {
      userAddedSharedWithEmpId.current.push(empId);
    }
    const empExists = taskObject.sharedWith.includes(empId);
    if (!empExists) {
      setTaskObject({
        ...taskObject,
        sharedWith: taskObject.sharedWith
          ? [...taskObject.sharedWith, `${empId}`]
          : [`${empId}`]
      });
      setShareObject({
        email: [...shareObject.email, emp.email],
        taskId: taskObject.taskId,
        userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`
      });
      setActiveSearch('');
      setSharedWithSearchTerm('');
    }
  };

  const isDeletable = useMemo(
    () =>
      (modalAction === 'EDIT' || modalAction === 'PARTIAL-EDIT') &&
      selectedFeedback &&
      (selectedFeedback.userId === authorizedUser.hcaid || isEnterpriseAdmin),
    [authorizedUser, isEnterpriseAdmin, modalAction, selectedFeedback]
  );

  const roundingTypesForAdHoc = useMemo(() => {
    const choices = [];
    if (isCNEdRounder) {
      choices.push('cned');
    }
    if (isCSCRounder) {
      choices.push('csc');
    }
    if (isNonSupportRounder) {
      choices.push('employee', 'patient');
    }
    return choices;
  }, [feedbackTypes]);

  const unitName = useMemo(() => {
    const foundUnit: Unit[] = units.filter(
      (unit: Unit) => unit.unitId === taskObject.unitId
    );
    return foundUnit[0]?.unit;
  }, [taskObject, units]);

  const sharedWithDisplay = useMemo(
    () =>
      sharedEmployeesData
        ?.filter((emp: Employee) =>
          taskObject.sharedWith.includes(emp.employeeId?.toUpperCase())
        )
        .map((emp: Employee) => {
          const empId = emp.employeeId?.toUpperCase();
          return `${emp.firstName} ${emp.lastName} ${empId}`;
        }) ?? [],
    [sharedEmployeesData, taskObject.sharedWith]
  );

  // Clear data when unmount
  useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);

  /** Shared with - search */
  useEffect(() => {
    getEmployeesBySearch(dbSharedSearchTerm);
  }, [dbSharedSearchTerm]);

  useEffect(() => {
    const selectedCat: Category = categories.filter((category: Category) => {
      return category.name === taskObject.category;
    })[0];
    if (selectedCat) {
      setSubCategories(selectedCat.subCategories);
    } else {
      setSubCategories([]);
    }
  }, [taskObject.category, categories]);

  useEffect(() => {
    setRequiredFieldValues(taskObject);
  }, [taskObject.roundingType, taskObject.category, subCategories]);

  useEffect(() => {
    if (isRoundStarted && modalData) {
      setTaskObject({
        ...taskObject,
        ...(globalUserSection === 'patient'
          ? {
              accountNum: modalData.accountNum || (modalData.mrn as string),
              patientFirstName: (modalData.firstName as string) || '',
              patientLastName: (modalData.lastName as string) || '',
              patientFullName:
                modalData.firstName && modalData.lastName
                  ? `${modalData.firstName} ${modalData.lastName}`
                  : '',
              room: modalData.room,
              bed: modalData.bed || '',
              unit:
                units.find(unit => unit.unitId === modalData.unitId)?.unit ||
                '',
              unitId: modalData.unitId
            }
          : {}),
        ...(globalUserSection === 'employee' ||
        globalUserSection === 'csc' ||
        globalUserSection === 'cned'
          ? {
              employeeFirstName: (modalData.firstName as string) || '',
              employeeLastName: (modalData.lastName as string) || '',
              employeeFullName: modalData.firstName
                ? `${modalData.lastName}, ${modalData.firstName}`
                : '',
              employeeId: modalData.hcaid as string,
              email: modalData.email || '',
              department: modalData.department,
              departmentCode: modalData.departmentCode,
              departmentId: modalData.departmentId
            }
          : {})
      });
      setRequiredFields(
        globalUserSection === 'patient'
          ? { ...requiredFields, unitId: modalData.unitId }
          : {
              ...requiredFields,
              employeeFirstName: modalData.employeeFirstName
            }
      );
    } else {
      setTaskObject({
        ...taskObject
      });
    }
  }, []);

  useEffect(() => {
    if (
      (modalAction === 'EDIT' || modalAction === 'PARTIAL-EDIT') &&
      selectedFeedback
    ) {
      /**
       * In order to add sharedWith property to the feedback object if its not present in the
       * selected feedback for edit/partial edit.
       */
      const tempFeedback = selectedFeedback.sharedWith
        ? { ...selectedFeedback }
        : { ...selectedFeedback, sharedWith: [] };
      setTaskObject({ ...tempFeedback } as any);
      setSelectedTaskSubtype(selectedFeedback.taskSubtype);
      setRequiredFieldValues(selectedFeedback as any);
    }
  }, [selectedFeedback, modalAction]);

  useEffect(() => {
    if (taskObject?.sharedWith && taskObject.sharedWith.length > 0) {
      getEmployeesByIds([...taskObject.sharedWith]);
    }
  }, [taskObject.sharedWith]);

  // For updating the feedback category list based on the selection
  useEffect(() => {
    if (
      taskObject.roundingType &&
      feedbackTypes.includes(taskObject.roundingType) &&
      (selectedTaskSubtype || taskObject.taskSubtype)
    ) {
      let categoryFor;
      switch (taskObject.roundingType) {
        case 'patient':
          categoryFor = 'patient';
          break;
        default:
          categoryFor = 'employee';
          break;
      }
      dispatch(
        getCategoriesByType({
          categoryFor,
          categoryType: 'feedback',
          feedbackType: selectedTaskSubtype || taskObject.taskSubtype
        })
      );
    }
  }, [selectedTaskSubtype, taskObject.taskSubtype, taskObject.roundingType]);

  // Validate fields on change and disable button
  useEffect(() => {
    let count = 0;
    Object.keys(requiredFields).forEach(item => {
      const tempVar = requiredFields[item];
      if (
        tempVar === '' ||
        tempVar === undefined ||
        (Array.isArray(tempVar) && tempVar.length === 0)
      ) {
        count += 1;
      }
      if (count === 0) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    });
  }, [requiredFields]);

  useEffect(() => {
    if (selectedFeedback) {
      if (selectedFeedback?.roundId) {
        dispatch(
          getRound({
            roundId: selectedFeedback?.roundId,
            fromHistoryDrawer: false,
            type: selectedFeedback?.roundingType,
            ...(selectedFeedback.roundingType === 'patient'
              ? {
                  accountNum: selectedFeedback.accountNum,
                  unitId: selectedFeedback.unitId
                }
              : {})
          })
        );
      } else if (
        !selectedFeedback?.roundId &&
        !(modalAction === 'UNIVERSAL-ADD' && modalType === 'Add Feedback')
      ) {
        dispatch(clearRoundState());
      }
    }
  }, [selectedFeedback]);

  /* Code block for navigating to round from issue modal */
  useEffect(() => {
    if (
      taskObject &&
      taskObject?.roundId &&
      (modalAction === 'VIEW' ||
        modalAction === 'EDIT' ||
        modalAction === 'PARTIAL-EDIT')
    ) {
      if (taskObject?.roundId) {
        dispatch(
          getRound({
            roundId: taskObject.roundId,
            fromHistoryDrawer: false,
            type: taskObject.roundingType,
            ...(taskObject.roundingType === 'patient'
              ? { accountNum: taskObject.accountNum, unitId: taskObject.unitId }
              : {})
          })
        );
      }
    }
  }, []);

  return (
    <FeedbackCard
      id="Feedback-Modal-Card"
      unit="card"
      className="show-modal"
      ref={modalContentRef}
    >
      {modalAction === 'VIEW' && selectedFeedback && (
        <FeedbackModalView
          feedbackInfo={selectedFeedback}
          employeeSharedWith={sharedEmployeesData}
          onClose={handleClose}
          userSection={globalUserSection}
          units={units}
          associatedPatient={{
            facilityId: taskObject?.facilityId,
            accountNum: taskObject?.accountNum
          }}
          handleRedirect={handleRedirect}
        />
      )}
      {modalAction !== 'VIEW' && (
        <>
          <CardHeader unit="header">
            <div>
              {(modalAction === 'ADD' ||
                modalAction === 'UNIVERSAL-ADD' ||
                (isRoundStarted && !selectedFeedback)) && (
                <TitleSpan>
                  <TitleIcon size="3x">campaign</TitleIcon>
                  Add Feedback
                </TitleSpan>
              )}
              {(modalAction === 'EDIT' || modalAction === 'PARTIAL-EDIT') &&
                selectedFeedback &&
                !selectedFeedback.completed && (
                  // TODO: This needs to go away in favor of shared button
                  <MarkCompleteButton
                    data-testid="feedback-modal-mark-complete-button"
                    kind="secondary"
                    icon="check_circle"
                    size="small"
                    onClick={() => handleMarkCompleted(selectedFeedback)}
                  >
                    Mark Complete
                  </MarkCompleteButton>
                )}
            </div>
            <NeuButton
              aria-label="close panel"
              data-testid="add-or-edit-feedback-modal-close-button"
              kind="tertiary"
              icon="close"
              onClick={() => {
                if (modalAction === 'UNIVERSAL-ADD') {
                  logTrackingEvent(logAdHocClick(modalType, 'close'));
                } else {
                  logTrackingEvent(logTaskClick('feedback', 'close'));
                }
                handleClose();
              }}
            >
              Close
            </NeuButton>
          </CardHeader>
          <ModalCardContent unit="content">
            {modalAction === 'UNIVERSAL-ADD' && (
              <>
                <InputHeading>Feedback Origin</InputHeading>
                <RoundingTypeDropdown
                  name="feedback-rounding-type"
                  open={isRoundingTypeOpen}
                  options={roundingTypesForAdHoc}
                  placeholder={
                    taskObject?.roundingType &&
                    roundingTypesForAdHoc.includes(taskObject?.roundingType)
                      ? formatRoundingType(taskObject?.roundingType)
                      : 'Select Origin'
                  }
                  selectedRoundingType={taskObject.roundingType ?? ''}
                  setSelectedRoundingType={changeFeedbackOrigin}
                  setOpen={setIsRoundingTypeOpen}
                />
              </>
            )}
            {modalAction === 'ADD' || modalAction === 'UNIVERSAL-ADD' ? (
              <>
                {taskObject.roundingType === 'patient' && (
                  <>
                    <InputHeading>Patient (Optional)</InputHeading>
                    <PatientSearch
                      chipVal={taskObject.patientFullName}
                      showLabel={false}
                      val={taskObject.patientFullName ?? ''}
                      handleRemovePatient={handleRemoveChip}
                      setPatientSelection={(patient: Patient) =>
                        setPatientForFeedback(patient)
                      }
                    />
                  </>
                )}
                {(taskObject.roundingType === 'employee' ||
                  taskObject.roundingType === 'csc' ||
                  taskObject.roundingType === 'cned') && (
                  <>
                    <InputHeading>Employee</InputHeading>
                    <EmployeeSearch
                      chipVal={taskObject.employeeFullName}
                      showLabel={false}
                      val={taskObject.employeeId}
                      handleRemoveEmployee={handleRemoveChip}
                      setEmployeeSelection={(emp: Employee) =>
                        setEmployeeForFeedback(emp)
                      }
                    />
                  </>
                )}
                {taskObject.roundingType === 'patient' && (
                  <>
                    <InputHeading>Unit</InputHeading>
                    <Dropdown
                      classes="px-0"
                      name="unitId"
                      open={isUnitOpen}
                      placeholder={
                        units.find(
                          (unit: Unit) => unit.unitId === taskObject.unitId
                        )?.unit || 'Select a Unit'
                      }
                      setOpen={setIsUnitOpen}
                    >
                      {units &&
                        units.map((unit: Unit) => (
                          <div
                            key={unit.unitId}
                            aria-selected={taskObject.unitId === unit.unitId}
                            className={`dropdown-hover-item${
                              taskObject.unitId === unit.unitId
                                ? ' selected'
                                : ''
                            }`}
                            role="option"
                            tabIndex={0}
                            onClick={() =>
                              handleDropdownChange(unit.unitId, 'unitId')
                            }
                            onKeyDown={() =>
                              handleDropdownChange(unit.unitId, 'unitId')
                            }
                          >
                            {unit.unit}
                          </div>
                        ))}
                    </Dropdown>
                  </>
                )}
              </>
            ) : (
              <>
                <FeedbackViewDiv2Column>
                  <FeedbackViewSubDiv style={{ flexBasis: '61%' }}>
                    {taskObject.roundingType === 'patient' ? (
                      <>
                        <InputHeading>Patient</InputHeading>
                        <LabelContentPatient
                          color={
                            taskObject &&
                            taskObject.facilityId &&
                            taskObject.accountNum &&
                            taskObject.roundId
                              ? '#206DA3'
                              : '#191919'
                          }
                          cursorType={
                            !!(
                              taskObject &&
                              taskObject.facilityId &&
                              taskObject.accountNum &&
                              taskObject.roundId
                            )
                          }
                          onClick={
                            taskObject &&
                            taskObject.facilityId &&
                            taskObject.accountNum &&
                            taskObject.roundId
                              ? () => {
                                  handleRedirect();
                                }
                              : undefined
                          }
                        >
                          {!taskObject.patientLastName ||
                          !taskObject.patientFirstName
                            ? 'Unknown'
                            : toTitleCase(
                                reverseName(
                                  taskObject.patientFirstName,
                                  taskObject.patientLastName
                                )
                              )}
                        </LabelContentPatient>
                      </>
                    ) : !(
                        modalType === 'Add Employee Feedback On Rounds' ||
                        modalType === 'Add Support Feedback On Rounds'
                      ) ? (
                      <>
                        <InputHeading>Employee</InputHeading>
                        <LabelContentPatient
                          className="neu-text-blue-60"
                          color={
                            taskObject &&
                            taskObject.employeeId &&
                            taskObject.roundId
                              ? '#206DA3'
                              : '#191919'
                          }
                          cursorType={
                            !!(
                              taskObject &&
                              taskObject.employeeId &&
                              taskObject.roundId
                            )
                          }
                          onClick={
                            taskObject &&
                            taskObject.employeeId &&
                            taskObject.roundId
                              ? () => {
                                  handleRedirect();
                                }
                              : undefined
                          }
                        >
                          {`${
                            taskObject.employeeLastName
                              ? `${toTitleCase(taskObject.employeeLastName)}, `
                              : ''
                          }${
                            taskObject.employeeFirstName
                              ? toTitleCase(taskObject.employeeFirstName)
                              : ''
                          }`}
                          {`${
                            taskObject.employeeId
                              ? ` | ${taskObject.employeeId}`
                              : 'Unknown'
                          }`}
                          {`${
                            !taskObject.employeeLastName &&
                            !taskObject.employeeFirstName
                              ? 'Unknown'
                              : ''
                          }`}
                        </LabelContentPatient>
                      </>
                    ) : (
                      <>
                        <InputHeading>Employee</InputHeading>
                        <EmployeeSearch
                          chipVal={taskObject.employeeFullName}
                          showLabel={false}
                          val={taskObject.employeeId}
                          handleRemoveEmployee={handleRemoveChip}
                          setEmployeeSelection={(emp: Employee) =>
                            setEmployeeForFeedback(emp)
                          }
                        />
                      </>
                    )}
                  </FeedbackViewSubDiv>
                  {taskObject.roundingType === 'patient' && !isRoundStarted && (
                    <FeedbackViewSubDiv>
                      <InputHeading>Room</InputHeading>
                      <LabelContentBold>
                        {taskObject.patientFirstName
                          ? taskObject.room
                            ? `${taskObject.room}${taskObject.bed || ''}`
                            : 'Unknown'
                          : 'N/A'}
                      </LabelContentBold>
                    </FeedbackViewSubDiv>
                  )}
                </FeedbackViewDiv2Column>
                {taskObject.roundingType === 'patient' && !isRoundStarted && (
                  <FeedbackViewDiv>
                    <InputHeading>Unit</InputHeading>
                    <LabelContentBold>
                      {`${taskObject.unitId ? unitName : 'Unknown'}`}
                    </LabelContentBold>
                  </FeedbackViewDiv>
                )}
                {taskObject.roundingType === 'employee' && !isRoundStarted && (
                  <FeedbackViewDiv>
                    <InputHeading>Department</InputHeading>
                    <LabelContentBold>
                      {`${
                        taskObject?.department
                          ? taskObject?.department
                          : 'Unknown'
                      }`}
                    </LabelContentBold>
                  </FeedbackViewDiv>
                )}
                {taskObject.roundingType === 'patient' && !isRoundStarted && (
                  <SearchResultsContainer>
                    <InputHeading>Employee</InputHeading>
                    <div className="pt-1 pb-2">
                      {(taskObject.employeeFirstName ||
                        taskObject.employeeLastName) && (
                        <BlueChip
                          key={taskObject.employeeId}
                          name={`${taskObject.employeeFirstName} ${
                            taskObject.employeeLastName
                          } ${
                            taskObject.employeeId
                              ? taskObject.employeeId.toString().toUpperCase()
                              : ''
                          }`}
                          removable={false}
                        />
                      )}
                    </div>
                  </SearchResultsContainer>
                )}
                {taskObject.roundingType === 'patient' && isRoundStarted && (
                  <>
                    <InputHeading>Unit</InputHeading>
                    <Dropdown
                      classes="px-0"
                      name="unitId"
                      open={isUnitOpen}
                      placeholder={
                        units.find(
                          (unit: Unit) => unit.unitId === taskObject.unitId
                        )?.unit || 'Select a Unit'
                      }
                      setOpen={setIsUnitOpen}
                    >
                      {units &&
                        units.map((unit: Unit) => (
                          <div
                            key={unit.unitId}
                            aria-selected={taskObject.unitId === unit.unitId}
                            className={`dropdown-hover-item${
                              taskObject.unitId === unit.unitId
                                ? ' selected'
                                : ''
                            }`}
                            role="option"
                            tabIndex={0}
                            onClick={() =>
                              handleDropdownChange(unit.unitId, 'unitId')
                            }
                            onKeyDown={() =>
                              handleDropdownChange(unit.unitId, 'unitId')
                            }
                          >
                            {unit.unit}
                          </div>
                        ))}
                    </Dropdown>
                  </>
                )}
                {!(modalType === 'Add Patient Feedback On Rounds') && (
                  <FeedbackViewSubDiv>
                    <InputHeading>Creator</InputHeading>
                    <LabelContentBold className="my-1">
                      {toTitleCase(taskObject.userFullName)}
                    </LabelContentBold>
                  </FeedbackViewSubDiv>
                )}
              </>
            )}

            {modalAction !== 'PARTIAL-EDIT' ? (
              <>
                <NeuRadio
                  data-testid="feedback-modal-task-subtype-radio-group"
                  className="mt-2"
                  unit="group"
                  label="Feedback Type"
                  name="taskSubtype"
                  required
                  value={selectedTaskSubtype || ''}
                  onClick={handleInputs}
                >
                  <NeuRadio
                    data-testid="feedback-modal-task-subtype-radio-item-recognition"
                    unit="item"
                    label="Recognition"
                    name="taskSubtype"
                    value="recognition"
                  />
                  <NeuRadio
                    data-testid="feedback-modal-task-subtype-radio-item-coaching"
                    unit="item"
                    label="Coaching"
                    name="taskSubtype"
                    value="coaching"
                  />
                </NeuRadio>
                <InputHeading>Feedback Category</InputHeading>
                {taskObject.taskCategory.length >= 1 ? (
                  <FeedbackViewDiv>
                    <div className="pt-1 pb-2">
                      {taskObject.taskCategory.map(category => {
                        return (
                          <BlueChip
                            key={category}
                            name={category}
                            removable={false}
                          />
                        );
                      })}
                    </div>
                  </FeedbackViewDiv>
                ) : (
                  <>
                    <Dropdown
                      id="Feedback-Modal-Category-Select"
                      classes="px-0"
                      name="category"
                      open={isCatOpen}
                      placeholder={
                        taskObject.category
                          ? taskObject.category
                          : 'Select a Category'
                      }
                      setOpen={setIsCatOpen}
                      disabled={
                        !taskObject.roundingType || !selectedTaskSubtype
                      }
                    >
                      {categories.map((category: Category) => (
                        <div
                          key={category.name}
                          aria-selected={category.name === taskObject.category}
                          className={`dropdown-hover-item${
                            category.name === taskObject.category
                              ? ' selected'
                              : ''
                          }`}
                          role="option"
                          tabIndex={0}
                          onClick={() =>
                            handleDropdownChange(category.name, 'category')
                          }
                          onKeyDown={() =>
                            handleDropdownChange(category.name, 'category')
                          }
                        >
                          {category.name}
                        </div>
                      ))}
                    </Dropdown>
                    <InputHeading>Feedback Subcategory</InputHeading>
                    <Dropdown
                      id="Feedback-Modal-Subcategory-Select"
                      classes="px-0"
                      name="subCategory"
                      open={isSubcatOpen}
                      placeholder={
                        taskObject.subCategory
                          ? taskObject.subCategory
                          : taskObject.category && subCategories.length === 0
                          ? 'No subcategories'
                          : 'Select a Subcategory'
                      }
                      setOpen={setIsSubcatOpen}
                      disabled={
                        !taskObject.category ||
                        (categories.length > 0 && subCategories.length === 0)
                      }
                    >
                      {subCategories.map((subcat: string) => (
                        <div
                          key={subcat}
                          aria-selected={subcat === taskObject.subCategory}
                          className={`dropdown-hover-item${
                            subcat === taskObject.subCategory ? ' selected' : ''
                          }`}
                          role="option"
                          tabIndex={0}
                          onClick={() =>
                            handleDropdownChange(subcat, 'subCategory')
                          }
                          onKeyDown={() =>
                            handleDropdownChange(subcat, 'subCategory')
                          }
                        >
                          {subcat}
                        </div>
                      ))}
                    </Dropdown>
                  </>
                )}
              </>
            ) : (
              <>
                <FeedbackViewDiv>
                  <InputHeading>Feedback Category</InputHeading>
                  <LabelContentBold>
                    {`${
                      taskObject.taskSubtype
                        ? toTitleCase(taskObject.taskSubtype)
                        : ''
                    } | `}
                    {taskObject.taskCategory?.join(', ')}
                  </LabelContentBold>
                </FeedbackViewDiv>
                {/* TODO: Add subCategory */}
              </>
            )}
            {(modalAction === 'ADD' ||
              modalAction === 'UNIVERSAL-ADD' ||
              isRoundStarted) &&
              taskObject.roundingType === 'patient' && (
                <>
                  <InputHeading>Employee</InputHeading>
                  <EmployeeSearch
                    chipVal={taskObject.employeeFullName}
                    showLabel={false}
                    val={taskObject.employeeId}
                    handleRemoveEmployee={handleRemoveChip}
                    setEmployeeSelection={(emp: Employee) =>
                      setEmployeeForFeedback(emp)
                    }
                  />
                </>
              )}

            {modalAction !== 'PARTIAL-EDIT' ? (
              <ModalTextArea
                className="mt-3"
                label="Details (Do not include PHI)"
                name="description"
                required
                spellCheck
                value={taskObject.description ? taskObject.description : ''}
                onNeuChange={handleInputs}
              />
            ) : (
              <FeedbackViewDiv>
                <InputHeading>Details</InputHeading>
                <LabelContentBold>
                  {taskObject.description ? taskObject.description : 'N/A'}
                </LabelContentBold>
              </FeedbackViewDiv>
            )}
            <InputHeading>Share With (Optional)</InputHeading>
            <div>
              {sharedWithDisplay.length > 0 && (
                <div className="pt-1 pb-2">
                  {sharedWithDisplay.map((emp: string) => {
                    return (
                      <ChipWrapper
                        key={emp}
                        onClick={() => handleRemoveSharedWith(emp)}
                      >
                        {taskObject.taskSubtype === 'recognition' &&
                          emp.split(/[\s+]+/).pop() ===
                            taskObject.employeeId.toUpperCase() && (
                            <BlueChip key={emp} name={emp} removable={false} />
                          )}
                        {taskObject.taskSubtype !== 'recognition' && (
                          <BlueChip key={emp} name={emp} />
                        )}
                        {taskObject.taskSubtype === 'recognition' &&
                          emp.split(/[\s+]+/).pop() !==
                            taskObject.employeeId.toUpperCase() && (
                            <BlueChip key={emp} name={emp} />
                          )}
                      </ChipWrapper>
                    );
                  })}
                </div>
              )}
              <Dropdown
                id="Feedback-Modal-Share-With"
                name="sharedWith"
                focused={activeSearch === 'sharedWith'}
                open={isSharedWithOpen}
                placeholder="Start typing name or 3-4 ID"
                query={sharedWithSearchTerm}
                typeAhead
                setOpen={setIsSharedWithOpen}
                setTypeAhead={setActiveSearch}
                setTypeAheadChange={handleSearching}
              >
                {employeesBySearchData?.map((emp: Employee) => (
                  <div key={emp.employeeId}>
                    <div
                      aria-selected={taskObject.sharedWith.includes(
                        emp.employeeId?.toUpperCase()
                      )}
                      className={`dropdown-hover-item${
                        taskObject.sharedWith.includes(
                          emp.employeeId?.toUpperCase()
                        )
                          ? ' selected'
                          : ''
                      }`}
                      role="option"
                      tabIndex={0}
                      onClick={
                        !taskObject.sharedWith.includes(
                          emp.employeeId?.toUpperCase()
                        )
                          ? () => setEmployeeToShare(emp)
                          : e => e.stopPropagation()
                      }
                      onKeyDown={
                        !taskObject.sharedWith.includes(
                          emp.employeeId?.toUpperCase()
                        )
                          ? () => setEmployeeToShare(emp)
                          : e => e.stopPropagation()
                      }
                    >
                      <div className="d-flex justify-content-between">
                        <p className="mt-1 mb-0">
                          {emp.firstName.toUpperCase()}{' '}
                          {emp.lastName.toUpperCase()}
                        </p>
                        <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                          {emp.employeeId}
                        </p>
                      </div>
                      <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                        {emp.title}
                      </p>
                    </div>
                    {employeesBySearchData?.length > 1 && (
                      <StyledDivider className="my-0" />
                    )}
                  </div>
                ))}
              </Dropdown>
            </div>
            {/* {modalAction !== 'UNIVERSAL-ADD' && modalAction !== 'ADD' && (
              <>
                <PopItem
                  id="Recognition"
                  role="option"
                  tabIndex={0}
                  onClick={() => {
                    window.open(
                      'https://colleaguerecognition.isrewards.com/',
                      '_blank'
                    );
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      window.open(
                        'https://colleaguerecognition.isrewards.com/',
                        '_blank'
                      );
                    }
                  }}
                >
                  <PopContent>
                    Recognition
                    <NeuIcon
                      style={{ paddingLeft: '5px' }}
                      color="blue"
                      size="1x"
                    >
                      launch
                    </NeuIcon>
                  </PopContent>
                </PopItem>
                <div style={{ fontSize: '12px' }}>
                  Points must be awarded via recognition site.
                </div>
              </>
            )} */}
          </ModalCardContent>
          <ModalFooter unit="footer" right={!isDeletable ? true : undefined}>
            {isDeletable && selectedFeedback && (
              <DeleteBtn
                modalContentRef={modalContentRef}
                task={selectedFeedback}
              />
            )}
            <NeuButton
              color="blue"
              size="small"
              data-testid="feedback-modal-save-button"
              onClick={handlePost}
              style={{ float: 'right' }}
              disabled={!showButton ? true : undefined}
            >
              Save Feedback
            </NeuButton>
          </ModalFooter>
        </>
      )}
    </FeedbackCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    categories: state.TaskReducer.categories,
    globalUserSection: state.UserReducer.userSection,
    isRoundStarted: state.RoundReducer.isRoundStarted,
    modalAction: state.ModalReducer.modalAction,
    modalData: state.ModalReducer.modalData,
    modalState: state.ModalReducer.modal,
    modalType: state.ModalReducer.modalType,
    selectedFeedback: state.TaskReducer.selectedData,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(AddFeedbackModal);
